import { useEffect, useId, useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CUSTOM_FIELD_TYPES,
  type AddCustomFieldInput,
  type CustomFieldModule,
  type CustomFieldType,
  type ResolvedCustomField,
} from '@tailoring-crm/shared';
import { isApiRequestError } from '../../lib/api/errors.js';
import {
  useAddCustomField,
  useUpdateCustomField,
} from '../../lib/custom-fields/use-custom-fields.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Textarea,
  Label,
  useToast,
} from '@tailoring-crm/ui';

/**
 * Adding a field to a module's form, or editing one.
 *
 * There is no key input, and that is the important thing about this form. The key is derived
 * from the name the shop first types and then never changes, so renaming a field two years later
 * still finds every value recorded under it. When editing, the key is shown read-only with the
 * reason, because a shop that cannot see it will eventually ask why renaming did not "reset"
 * anything.
 *
 * The TYPE is fixed after creation for the sharper version of the same reason: every value
 * already recorded went into the slot the type implied, and there is no way to move it. The
 * select is simply absent when editing rather than disabled — a control that cannot be used is
 * a control worth removing.
 *
 * A built-in gets a shorter form still: its name and whether it is shown. Everything else about
 * it is the product's rather than the shop's.
 */

interface FormValues {
  label: string;
  type: CustomFieldType;
  options: string;
  helperText: string;
  required: boolean;
  hidden: boolean;
  showInTable: boolean;
}

const EMPTY: FormValues = {
  label: '',
  type: 'text',
  options: '',
  helperText: '',
  required: false,
  hidden: false,
  // Off by default: the sheet is already wide, and a new field is far more often something to
  // record than something to scan a hundred rows of.
  showInTable: false,
};

function valuesFrom(field: ResolvedCustomField, label: string): FormValues {
  return {
    label,
    type: field.type ?? 'text',
    options: field.options.join('\n'),
    helperText: field.helperText ?? '',
    required: field.required,
    hidden: field.hidden,
    showInTable: field.showInTable,
  };
}

export interface CustomFieldFormDialogProps {
  module: CustomFieldModule;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Present for an edit; absent to add a new one. */
  field?: ResolvedCustomField | null;
  /** The resolved label of the field being edited, so a built-in shows its translated name. */
  currentLabel?: string;
  onSaved?: () => void;
}

export function CustomFieldFormDialog({
  module,
  open,
  onOpenChange,
  field = null,
  currentLabel = '',
  onSaved,
}: CustomFieldFormDialogProps): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const fieldId = useId();
  const ns = 'customFields';

  const isEdit = field !== null;
  const isBuiltin = field?.origin === 'builtin';
  const add = useAddCustomField(module);
  const update = useUpdateCustomField(module);

  const [values, setValues] = useState<FormValues>(EMPTY);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setValues(field ? valuesFrom(field, currentLabel) : EMPTY);
    setFieldErrors({});
    setFormError(null);
  }, [open, field, currentLabel]);

  const set = <K extends keyof FormValues>(key: K, value: FormValues[K]): void => {
    setValues((previous) => ({ ...previous, [key]: value }));
  };

  const optionList = (): string[] =>
    values.options
      .split('\n')
      .map((option) => option.trim())
      .filter((option) => option.length > 0);

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    void (async () => {
      setFieldErrors({});
      setFormError(null);

      try {
        if (isEdit) {
          await update.mutateAsync({
            fieldKey: field.key,
            input: {
              // A built-in reverts to its default name when the box is emptied, rather than
              // being saved as blank. That is the only way back to the translated name.
              label: values.label.trim() === '' ? null : values.label.trim(),
              hidden: values.hidden,
              showInTable: values.showInTable,
              ...(isBuiltin
                ? {}
                : {
                    options: optionList(),
                    helperText: values.helperText.trim() || null,
                    required: values.required,
                  }),
            },
          });
        } else {
          const input: AddCustomFieldInput = {
            label: values.label.trim(),
            type: values.type,
            options: values.type === 'select' ? optionList() : [],
            helperText: values.helperText.trim() || null,
            required: values.required,
            showInTable: values.showInTable,
          };
          await add.mutateAsync(input);
        }

        toast({
          title: isEdit ? t(`${ns}.saved`) : t(`${ns}.added`),
          variant: 'success',
        });
        onSaved?.();
        onOpenChange(false);
      } catch (error) {
        if (isApiRequestError(error) && error.fieldErrors) {
          const next: Record<string, string> = {};
          for (const [path, messages] of Object.entries(error.fieldErrors)) {
            next[path.split('.').pop() ?? path] = messages.join(' ');
          }
          setFieldErrors(next);
          setFormError(Object.keys(next).length > 0 ? null : error.message);
          return;
        }
        setFormError(error instanceof Error ? error.message : t('error.unexpected'));
      }
    })();
  };

  const pending = add.isPending || update.isPending;

  const errorFor = (key: string): ReactElement | null =>
    fieldErrors[key] ? (
      <p role="alert" className="text-sm font-medium text-destructive">
        {fieldErrors[key]}
      </p>
    ) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? t(`${ns}.editTitle`) : t(`${ns}.addTitle`)}</DialogTitle>
          <DialogDescription>
            {isBuiltin ? t(`${ns}.builtinDescription`) : t(`${ns}.formDescription`)}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          {formError ? (
            <p role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {formError}
            </p>
          ) : null}

          <div className="flex flex-col gap-2">
            <Label htmlFor={`${fieldId}-label`}>{t(`${ns}.label`)}</Label>
            <Input
              id={`${fieldId}-label`}
              value={values.label}
              onChange={(event) => set('label', event.target.value)}
              aria-invalid={fieldErrors.label ? true : undefined}
              autoFocus
            />
            {isBuiltin ? (
              <p className="text-sm text-muted-foreground">
                {t(`${ns}.builtinLabelHint`, { name: field.defaultLabel })}
              </p>
            ) : null}
            {errorFor('label')}
          </div>

          {/* The key: shown, never editable, with the reason. */}
          {isEdit ? (
            <div className="flex flex-col gap-2">
              <Label htmlFor={`${fieldId}-key`}>{t(`${ns}.key`)}</Label>
              <code
                id={`${fieldId}-key`}
                className="rounded-md bg-muted px-2 py-1.5 font-mono text-sm text-muted-foreground"
              >
                {field.key}
              </code>
              <p className="text-sm text-muted-foreground">{t(`${ns}.keyExplainer`)}</p>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <Label htmlFor={`${fieldId}-type`}>{t(`${ns}.type.legend`)}</Label>
              <select
                id={`${fieldId}-type`}
                value={values.type}
                onChange={(event) => set('type', event.target.value as CustomFieldType)}
                className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              >
                {CUSTOM_FIELD_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {t(`${ns}.type.${type}`)}
                  </option>
                ))}
              </select>
              <p className="text-sm text-muted-foreground">{t(`${ns}.typeHint`)}</p>
            </div>
          )}

          {!isBuiltin && values.type === 'select' ? (
            <div className="flex flex-col gap-2">
              <Label htmlFor={`${fieldId}-options`}>{t(`${ns}.options`)}</Label>
              <Textarea
                id={`${fieldId}-options`}
                rows={4}
                value={values.options}
                onChange={(event) => set('options', event.target.value)}
              />
              <p className="text-sm text-muted-foreground">{t(`${ns}.optionsHint`)}</p>
              {errorFor('options')}
            </div>
          ) : null}

          {!isBuiltin ? (
            <div className="flex flex-col gap-2">
              <Label htmlFor={`${fieldId}-helper`}>{t(`${ns}.helperText`)}</Label>
              <Input
                id={`${fieldId}-helper`}
                value={values.helperText}
                onChange={(event) => set('helperText', event.target.value)}
              />
              <p className="text-sm text-muted-foreground">{t(`${ns}.helperTextHint`)}</p>
            </div>
          ) : null}

          <div className="flex flex-col gap-3 rounded-lg border border-border p-3">
            {!isBuiltin ? (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={values.required}
                  onChange={(event) => set('required', event.target.checked)}
                  className="size-4 rounded border-input"
                />
                {t(`${ns}.requiredLabel`)}
              </label>
            ) : null}

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={values.showInTable}
                onChange={(event) => set('showInTable', event.target.checked)}
                className="size-4 rounded border-input"
              />
              {t(`${ns}.showInTableLabel`)}
            </label>

            {isEdit ? (
              <label
                className="flex items-center gap-2 text-sm"
                title={field.canHide ? undefined : t(`${ns}.cannotHide`)}
              >
                <input
                  type="checkbox"
                  checked={values.hidden}
                  disabled={!field.canHide}
                  onChange={(event) => set('hidden', event.target.checked)}
                  className="size-4 rounded border-input disabled:opacity-50"
                />
                <span className={field.canHide ? undefined : 'text-muted-foreground'}>
                  {t(`${ns}.hiddenLabel`)}
                </span>
              </label>
            ) : null}

            {isEdit && !field.canHide ? (
              <p className="text-sm text-muted-foreground">{t(`${ns}.cannotHide`)}</p>
            ) : null}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={pending}>
              {isEdit ? t('common.save') : t(`${ns}.add`)}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
